import { useState, useMemo } from "react";
import { Check, Truck, DollarSign, Wrench, CheckSquare, FileText, SmilePlus } from "lucide-react";
import GuideBanner from "../components/GuideBanner";
import Badge from "../components/Badge";
import Pagination from "../components/Pagination";
import { CLIENT_STAGES, PARTNER_STAGES, PARTNER_TYPES, FLEET_SIZES, CONTRACT_STATUSES } from "../constants";
import { fmt, fmtShort, fmtDate } from "../utils";

export default function Dashboard({
  clients, partners, tasks, today, toggleTask,
  overdueTasks, todayTasks, upcomingTasks, clientKpis, partnerKpis,
  contracts, satisfaction,
  setSelClient, setSelPartner, setShowNewInteraction, setMod,
}) {

  // Enriquece uma tarefa com os dados do parceiro/cliente vinculado (refId)
  const enrichTask = (t) => {
    if (t.type === "partner") {
      const entity = (partners || []).find((p) => p.id === t.refId);
      if (!entity) return null;
      const type = PARTNER_TYPES.find((x) => x.id === entity.type);
      return {
        kind: "partner", entity,
        typeLabel: type ? `${type.label}` : "Parceiro",
        name: entity.name, contact: entity.contact, phone: entity.phone,
      };
    }
    if (t.type === "client") {
      const entity = (clients || []).find((c) => c.id === t.refId);
      if (!entity) return null;
      return {
        kind: "client", entity,
        typeLabel: "Cliente (Frota)",
        name: entity.company, contact: entity.contact, phone: entity.phone,
      };
    }
    return null;
  };

  // Atrasadas paginadas: a fila passou de centenas e empurrava o resto do painel
  // para fora da tela. Se a lista encolher (tarefa resolvida), a página não fica vazia.
  const [pagAtrasadas, setPagAtrasadas] = useState(1);
  const [tamAtrasadas, setTamAtrasadas] = useState(10);
  const totalPagAtrasadas = Math.max(1, Math.ceil(overdueTasks.length / tamAtrasadas));
  const paginaAtrasadas = Math.min(pagAtrasadas, totalPagAtrasadas);
  const atrasadasDaPagina = overdueTasks.slice((paginaAtrasadas - 1) * tamAtrasadas, paginaAtrasadas * tamAtrasadas);

  // "Feito" numa tarefa atrasada → abre modal para atualizar o contato.
  // Sem vínculo (refId/tipo), apenas marca como concluída.
  const handleOverdueDone = (t) => {
    const info = enrichTask(t);
    if (info && setShowNewInteraction) {
      setShowNewInteraction({ type: info.kind, id: info.entity.id, taskId: t.id });
    } else {
      toggleTask(t.id);
    }
  };

  // Contract KPIs
  const contractKpis = useMemo(() => {
    const enriched = (contracts || []).map((c) => {
      let status = c.status;
      if (status === "ativo" && c.endDate) {
        const daysLeft = Math.ceil((new Date(c.endDate) - new Date(today)) / 86400000);
        if (daysLeft < 0) status = "vencido";
        else if (daysLeft <= 30) status = "vencendo";
      }
      return { ...c, computedStatus: status };
    });
    const active = enriched.filter((c) => c.computedStatus === "ativo");
    const expiring = enriched.filter((c) => c.computedStatus === "vencendo");
    const mrr = active.reduce((s, c) => s + (parseFloat(c.value) || 0), 0) +
                expiring.reduce((s, c) => s + (parseFloat(c.value) || 0), 0);
    return { total: enriched.length, active: active.length, expiring: expiring.length, mrr };
  }, [contracts, today]);

  // NPS
  const npsScore = useMemo(() => {
    const sat = satisfaction || [];
    if (sat.length === 0) return null;
    const promoters = sat.filter((s) => s.score >= 9).length;
    const detractors = sat.filter((s) => s.score < 7).length;
    return Math.round(((promoters - detractors) / sat.length) * 100);
  }, [satisfaction]);

  // Alertas de retorno de contato (clients + partners com nextActionDate <= today)
  const contactAlerts = useMemo(() => {
    const alerts = [];
    (clients || []).forEach((c) => {
      if (c.nextActionDate && c.nextActionDate <= today && !["ganho", "perdido", "descartado"].includes(c.stage)) {
        alerts.push({ type: "client", entity: c, label: c.company, action: c.nextAction, date: c.nextActionDate, overdue: c.nextActionDate < today });
      }
    });
    (partners || []).forEach((p) => {
      if (p.nextActionDate && p.nextActionDate <= today && !["ativo", "recusado"].includes(p.stage)) {
        alerts.push({ type: "partner", entity: p, label: p.name, action: p.nextAction, date: p.nextActionDate, overdue: p.nextActionDate < today });
      }
    });
    return alerts.sort((a, b) => a.date.localeCompare(b.date));
  }, [clients, partners, today]);

  // Upcoming follow-ups de retorno de contato (futuro)
  const upcomingFollowups = useMemo(() => {
    const items = [];
    (clients || []).forEach((c) => {
      if (c.nextActionDate && c.nextActionDate > today && !["ganho", "perdido", "descartado"].includes(c.stage)) {
        items.push({ type: "client", entity: c, label: c.company, action: c.nextAction, date: c.nextActionDate });
      }
    });
    (partners || []).forEach((p) => {
      if (p.nextActionDate && p.nextActionDate > today && !["ativo", "recusado"].includes(p.stage)) {
        items.push({ type: "partner", entity: p, label: p.name, action: p.nextAction, date: p.nextActionDate });
      }
    });
    return items.sort((a, b) => a.date.localeCompare(b.date));
  }, [clients, partners, today]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <GuideBanner
        title="Bem-vindo ao CRM — Seu painel diário de trabalho"
        text="Aqui você vê tudo que precisa fazer hoje: follow-ups atrasados, tarefas do dia e um resumo das duas frentes (Clientes e Parceiros). Comece pelas tarefas atrasadas, depois siga para as do dia."
      />

      {/* Alertas urgentes de tarefas */}
      {overdueTasks.length > 0 && (
        <div style={{ background: "#fef2f2", borderRadius: 10, padding: "14px 18px", border: "1px solid #fecaca" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#991b1b", marginBottom: 8 }}>
            {overdueTasks.length} tarefa(s) ATRASADA(S) — resolva agora!
          </div>
          {atrasadasDaPagina.map((t) => {
            const info = enrichTask(t);
            return (
              <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: "1px solid #fecaca50" }}>
                <div
                  style={{ minWidth: 0, flex: 1, cursor: info ? "pointer" : "default" }}
                  onClick={() => info && (info.kind === "client" ? setSelClient?.(info.entity) : setSelPartner?.(info.entity))}
                >
                  {info ? (
                    <>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#991b1b" }}>
                        {info.name}
                        <span style={{ fontSize: 10, fontWeight: 500, color: "#b45309", marginLeft: 6 }}>· {info.typeLabel}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#b91c1c", marginTop: 1 }}>{t.title}</div>
                      <div style={{ fontSize: 10, color: "#7f1d1d", marginTop: 1 }}>
                        {info.contact ? `${info.contact}` : "Sem contato"}
                        {info.phone ? ` · ${info.phone}` : ""}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: "#991b1b" }}>{t.title}</div>
                  )}
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                  <span style={{ fontSize: 10, color: "#ef4444", whiteSpace: "nowrap" }}>{fmtShort(t.dueDate)}</span>
                  <button onClick={() => handleOverdueDone(t)} style={{ padding: "4px 9px", borderRadius: 5, border: "none", background: "#059669", color: "#fff", fontSize: 10, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                    {info ? "✓ Atualizar contato" : "✓ Feito"}
                  </button>
                </div>
              </div>
            );
          })}
          {overdueTasks.length > 10 && (
            <div style={{ margin: "8px -18px -14px" }}>
              <Pagination page={paginaAtrasadas} totalItems={overdueTasks.length} pageSize={tamAtrasadas}
                onPageChange={setPagAtrasadas} onPageSizeChange={setTamAtrasadas} sizes={[10, 25, 50, 100]} />
            </div>
          )}
        </div>
      )}

      {/* Alertas de retorno de contato */}
      {contactAlerts.length > 0 && (
        <div style={{ background: "#fffbeb", borderRadius: 10, padding: "14px 18px", border: "1px solid #fde68a" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>
            {contactAlerts.length} contato(s) para retornar hoje
          </div>
          {contactAlerts.map((a, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #fde68a50", cursor: "pointer" }}
              onClick={() => a.type === "client" ? setSelClient?.(a.entity) : setSelPartner?.(a.entity)}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: a.overdue ? "#991b1b" : "#78350f" }}>
                  {a.label}
                  {a.overdue && <span style={{ fontSize: 10, color: "#ef4444", marginLeft: 6 }}>ATRASADO</span>}
                </div>
                <div style={{ fontSize: 10, color: "#92400e" }}>{a.action}</div>
              </div>
              <span style={{ fontSize: 10, color: "#92400e" }}>{fmtShort(a.date)}</span>
            </div>
          ))}
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {[
          { label: "Leads Clientes Ativos", value: clientKpis.active, color: "#2563eb", icon: Truck, sub: `${clientKpis.won} ganhos` },
          { label: "Pipeline Estimado", value: fmt(clientKpis.pipeline), color: "#0d9488", icon: DollarSign },
          { label: "Parceiros na Rede", value: partnerKpis.active, color: "#7c3aed", icon: Wrench, sub: `${partnerKpis.inProgress} em progresso` },
          { label: "Tarefas Hoje", value: todayTasks.length, color: "#f59e0b", icon: CheckSquare, sub: `${overdueTasks.length} atrasadas` },
          { label: "Contratos Ativos", value: contractKpis.active, color: "#059669", icon: FileText, sub: contractKpis.expiring > 0 ? `${contractKpis.expiring} vencendo` : `Receita: ${fmt(contractKpis.mrr)}/mês` },
          { label: "NPS Score", value: npsScore !== null ? npsScore : "—", color: npsScore >= 50 ? "#059669" : npsScore >= 0 ? "#f59e0b" : "#ef4444", icon: SmilePlus, sub: `${(satisfaction || []).length} respostas` },
        ].map((k, i) => (
          <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "14px 16px", border: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: 7, background: k.color + "10", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <k.icon size={14} color={k.color} />
              </div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>{k.value}</div>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 1 }}>{k.label}</div>
            {k.sub && <div style={{ fontSize: 9, color: "#94a3b8" }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Tarefas do dia + Follow-ups */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Tarefas de Hoje ({todayTasks.length})</div>
          {todayTasks.length === 0 ? (
            <div style={{ fontSize: 12, color: "#94a3b8", padding: "20px 0", textAlign: "center" }}>Nenhuma tarefa para hoje </div>
          ) : (
            todayTasks.map((t) => (
              <div key={t.id} style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "8px 0", borderBottom: "1px solid #f1f5f9" }}>
                <button onClick={() => toggleTask(t.id)} style={{ width: 18, height: 18, borderRadius: 4, border: "2px solid #d1d5db", background: "transparent", cursor: "pointer", flexShrink: 0, marginTop: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {t.done && <Check size={12} color="#059669" />}
                </button>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#1e293b" }}>{t.title}</div>
                  {t.notes && <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>{t.notes}</div>}
                </div>
              </div>
            ))
          )}
        </div>
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Próximos Follow-ups</div>
          {upcomingTasks.length === 0 && upcomingFollowups.length === 0 ? (
            <div style={{ fontSize: 12, color: "#94a3b8", padding: "20px 0", textAlign: "center" }}>Nenhum follow-up agendado</div>
          ) : (
            <>
              {/* Follow-ups de contatos com data */}
              {upcomingFollowups.slice(0, 4).map((f, i) => (
                <div key={`fu-${i}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                  onClick={() => f.type === "client" ? setSelClient?.(f.entity) : setSelPartner?.(f.entity)}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {f.label}
                    </div>
                    <div style={{ fontSize: 9, color: "#94a3b8" }}>{f.action}</div>
                  </div>
                  <span style={{ fontSize: 10, color: "#64748b", flexShrink: 0, marginLeft: 8 }}>{fmtShort(f.date)}</span>
                </div>
              ))}
              {/* Tarefas agendadas */}
              {upcomingTasks.slice(0, 4).map((t) => (
                <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid #f1f5f9" }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: "#1e293b", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.title}</div>
                  <span style={{ fontSize: 10, color: "#64748b", flexShrink: 0, marginLeft: 8 }}>{fmtShort(t.dueDate)}</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Overview por frente */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 14 }}>
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Frente 1 — Clientes por Estágio</div>
          {CLIENT_STAGES.filter((s) => !["ganho", "perdido", "descartado"].includes(s.id)).map((s) => (
            <div key={s.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: 2, background: s.color }} />
                <span style={{ fontSize: 11, fontWeight: 500, color: "#475569" }}>{s.label}</span>
              </div>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{clientKpis.byStage[s.id] || 0}</span>
            </div>
          ))}
        </div>
        <div style={{ background: "#fff", borderRadius: 10, padding: "16px 18px", border: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 12 }}>Frente 2 — Parceiros por Tipo</div>
          {PARTNER_TYPES.map((t) => (
            <div key={t.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0" }}>
              <span style={{ fontSize: 11, color: "#475569" }}>{t.label}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{partnerKpis.byType[t.id] || 0}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
